import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Link,
  Paper,
  TextField,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import add from "/src/assets/icons/add.svg";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import Checkbox from "@mui/material/Checkbox";
import PoetIcon from "components/PoetIcon/PoetIcon";
import AddCodeGroupFrom from "../AddCodeGroupFrom";
import ProcedureCodeDialog from "components/shared/Dialogs/ProcedureCodeDialog";
import Api from "api";
import customizedTheme from "styles/theme";
import { getCodeGroupIdentity } from "../Utils/codeGroupUtils";

export default function ProcedureGroupTable({
  codeGroups = [],
  setCodeGroups,
  healthPlanKey,
  onRemoveCodeGroup,
  missingCoverageGroupIdentities = [],
}) {
  const theme = useTheme();
  const warningIconColor =
    theme.palette.warningIcon?.main || customizedTheme.palette.warningIcon.main;
  const [showAddCodeGroup, setShowAddCodeGroup] = useState(false);
  const boxRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState(window.innerHeight);
  const [viewCodesOpen, setViewCodesOpen] = useState(false);
  const [viewCodesList, setViewCodesList] = useState([]);

  const handleAddGroup = () => {
    setShowAddCodeGroup(true);
  };

  useEffect(() => {
    function updateHeight() {
      if (boxRef.current) {
        const offset = boxRef.current.offsetTop;
        setMaxHeight(window.innerHeight - offset - 32); // 32 for padding/margin
      }
    }
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  const updateCoverageCriteriaList = (cc, index) => {
    setCodeGroups((prevGroups) => {
      const updatedGroups = [...prevGroups];
      updatedGroups[index] = {
        ...updatedGroups[index],
        coverageCriteriaList: cc,
      };
      return updatedGroups;
    });
  };
  const updateisPAonly = (isPaOnly, index) => {
    setCodeGroups((prevGroups) => {
      const updatedGroups = [...prevGroups];
      updatedGroups[index] = {
        ...updatedGroups[index],
        isPaOnly: isPaOnly,
      };
      return updatedGroups;
    });
  };

  /**
   * Loads procedure codes for a code group and opens the read-only viewer dialog.
   *
   * @param {Object} group Procedure code group row data.
   * @returns {Promise<void>}
   */
  const handleViewCodesClick = async (group) => {
    try {
      const response = await Api.CodeGroup.getCodeGroupDetails(
        group.codeGroupKey,
        "procedure"
      );
      const expandedCodes = response?.data?.expandedCodes || [];

      setViewCodesList(
        expandedCodes.map((item) => ({
          code: item?.code || "",
          description: item?.description || "",
        }))
      );
    } catch (error) {
      console.error("Error fetching procedure code group details:", error);
      setViewCodesList([]);
    } finally {
      setViewCodesOpen(true);
    }
  };

  /**
   * Closes the procedure read-only viewer dialog.
   *
   * @returns {void}
   */
  const handleCloseViewCodesDialog = () => {
    setViewCodesOpen(false);
  };

  return (
    <Box>
      <AddCodeGroupFrom
        codeGroupType="Procedure"
        healthPlanKey={healthPlanKey}
        open={showAddCodeGroup}
        onClose={() => setShowAddCodeGroup(false)}
        existingCodeGroupKeys={codeGroups
          .map((codeGroup) => codeGroup.codeGroupKey)
          .filter((codeGroupKey) => codeGroupKey != null)}
        updateCodeGroupList={(newGroups) => {
          if (newGroups && newGroups.length > 0)
            setCodeGroups([...newGroups, ...codeGroups]);
        }}
      />
      {/* Header */}
      <Box display="flex" alignItems="center" mb={2}>
        <Tooltip title="Add Procedure Group">
          <IconButton onClick={handleAddGroup} color="primary">
            <PoetIcon iconName="add" />
          </IconButton>
        </Tooltip>
        <Typography variant="h6" sx={{ fontWeight: "bold", ml: 1 }}>
          Add Procedure Group
        </Typography>
      </Box>

      {/* Column Headings */}
      <Paper elevation={0} sx={{ p: 2, mb: 1, backgroundColor: "#f5f5f5" }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={3} sx={{ width: "20%" }}>
            <Typography fontWeight="bold">Code Group</Typography>
          </Grid>
          <Grid item xs={3} sx={{ width: "20%" }}>
            <Typography fontWeight="bold">Code(s)</Typography>
          </Grid>
          <Grid item xs={2} sx={{ width: "10%" }}>
            <Typography fontWeight="bold">CC</Typography>
          </Grid>
          {/* <Grid item xs={4} sx={{ width: "12%" }}>
            <Typography fontWeight="bold">Multi Policy</Typography>
          </Grid> */}
          <Grid item xs={4} sx={{ width: "8%" }}>
            <Typography fontWeight="bold">PA Only</Typography>
          </Grid>
          <Grid item xs={4} sx={{ width: "12%" }}>
            <Typography fontWeight="bold">Description</Typography>
          </Grid>
          <Grid item xs={1} sx={{ width: "8%", textAlign: "right" }}>
            <Typography fontWeight="bold">Remove</Typography>
          </Grid>
        </Grid>
      </Paper>

      <Box ref={boxRef} sx={{ maxHeight: maxHeight, overflowY: "auto" }}>
        {/* Data Rows */}
        {codeGroups.map((group, index) => {
          const hasCoverageError =
            missingCoverageGroupIdentities.includes(
              getCodeGroupIdentity(group)
            ) && String(group.coverageCriteriaList ?? "").trim() === "";

          return (
            <Paper
              key={index}
              variant="outlined"
              sx={{ mb: 2, p: 2, backgroundColor: "#fafafa" }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={3} sx={{ width: "20%" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                    >
                      {group.codeGroupName}
                    </Typography>
                    {group.isLatestVersion === false && (
                      <Tooltip title="A newer version of this code group is available">
                        <div>
                          <PoetIcon
                            iconName="warning"
                            color={warningIconColor}
                          />
                        </div>
                      </Tooltip>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={3} sx={{ width: "20%" }}>
                  <Link
                    component="button"
                    type="button"
                    underline="hover"
                    sx={{ display: "flex", alignItems: "center" }}
                    onClick={() => handleViewCodesClick(group)}
                  >
                    <VisibilityIcon sx={{ mr: 1 }} />
                    View Codes ({`${group.codeCount ? group.codeCount : 0}`})
                  </Link>
                </Grid>
                <Grid item xs={2} sx={{ width: "10%" }}>
                  <Typography
                    sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                  >
                    <TextField
                      data-testid={`coverage-criteria-${index}`}
                      value={group.coverageCriteriaList}
                      onChange={(e) => {
                        updateCoverageCriteriaList(e.target.value, index);
                      }}
                      error={hasCoverageError}
                      helperText={hasCoverageError ? "CC required" : ""}
                      variant="outlined"
                      size="small"
                      fullWidth
                    />

                    {/* {group.coverageCriteriaList} */}
                  </Typography>
                </Grid>
                {/* <Grid item xs={4} sx={{ width: "12%" }}>
                <Typography
                  sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                >
                  {group.multiPolicy}
                </Typography>
              </Grid> */}
                <Grid item xs={4} sx={{ width: "8%" }}>
                  <Checkbox
                    data-testid={`pa-only-checkbox-${index}`}
                    checked={group.isPaOnly}
                    onChange={(e) => {
                      updateisPAonly(e.target.checked, index);
                    }}
                  />
                </Grid>
                <Grid item xs={4} sx={{ width: "12%" }}>
                  <Typography
                    sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                  >
                    {group.codeGroupDescription}
                  </Typography>
                </Grid>
                <Grid item xs={1} sx={{ width: "8%", textAlign: "right" }}>
                  <Tooltip
                    title={
                      group.hasImmutableRule
                        ? "Code Group is linked to an immutable rule"
                        : "Remove from Policy"
                    }
                  >
                    <span>
                      <IconButton
                        aria-label={`Remove ${group.codeGroupName} from policy`}
                        data-testid={`remove-code-group-${index}`}
                        disabled={group.hasImmutableRule}
                        onClick={() => onRemoveCodeGroup?.(group)}
                        size="small"
                        sx={{ color: "error.main" }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Grid>
              </Grid>
            </Paper>
          );
        })}
      </Box>
      <ProcedureCodeDialog
        open={viewCodesOpen}
        onClose={handleCloseViewCodesDialog}
        existingCodes={viewCodesList}
        readOnly={true}
      />
    </Box>
  );
}
